/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import { useLanguage } from '@/providers/LanguageProvider';

import { CustomButton } from '@/components/common';

import { DateRange, DateRangeUpdate } from './types';
import { areRangesEqual } from './utils';

interface DateRangeActionsProps {
  setIsOpen: (open: boolean) => void;
  range: DateRange;
  rangeCompare?: DateRange;
  openedRangeRef: React.MutableRefObject<DateRange | undefined>;
  openedRangeCompareRef: React.MutableRefObject<DateRange | undefined>;
  onUpdate: (values: DateRangeUpdate) => void;
  openedMostRecentRef: React.MutableRefObject<number | null>;
  openedPresetRef: React.MutableRefObject<string | undefined>;
  selectedPreset?: string;
  resetValues: () => void;
  selectedMostRecent?: number | null;
  isInvalid?: boolean;
}

export const DateRangeActions: React.FC<DateRangeActionsProps> = ({
  setIsOpen,
  range,
  rangeCompare,
  openedRangeRef,
  openedRangeCompareRef,
  onUpdate,
  resetValues,
  selectedMostRecent = null,
  openedMostRecentRef,
  openedPresetRef,
  selectedPreset,
  isInvalid = false,
}) => {
  const { messages } = useLanguage();
  const langData = messages?.PromptFilters;

  const hasRangeChanged =
    !areRangesEqual(range, openedRangeRef.current) ||
    !areRangesEqual(rangeCompare, openedRangeCompareRef.current);

  const hasMostRecentChanged =
    selectedMostRecent !== openedMostRecentRef.current ||
    selectedPreset !== openedPresetRef.current;

  return (
    <div className="mt-4 flex items-center border-t border-t-line-primary pt-5">
      <div className="flex gap-4">
        <CustomButton
          customSize="regular"
          customVariant="primary"
          disabled={isInvalid}
          onClick={() => {
            setIsOpen(false);
            if (hasRangeChanged || hasMostRecentChanged) {
              onUpdate({ range, rangeCompare, mostRecent: selectedMostRecent });
            }
          }}
        >
          {langData?.date_range.apply}
        </CustomButton>

        <CustomButton
          customSize="regular"
          customVariant="secondary"
          onClick={() => {
            setIsOpen(false);
            resetValues();
          }}
        >
          {messages?.General?.cancel}
        </CustomButton>
      </div>
    </div>
  );
};
